import _ from 'underscore';
import { getLength } from '@bingads-webui-universal/primitive-utilities';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { getODataConfig } from '@bingads-webui-campaign-react/perf-max-recommendation';

import { MaxDescriptionCount, MaxHeadlineCount, MaxLongHeadlineCount, MinDescriptionCount, MinHeadlineCount, MinLongHeadlineCount } from './const';
import { getFlattenedImageAssetList, updateAssetsIdsAsync, setMTImageModel } from './components/image-asset-model-util';

const WhitelistedPropertyKeys = [
  'Id',
  'Name',
  'Status',
  'Campaign',
  'FinalUrls',
  'MobileFinalUrls',
  'Images',
  'Headlines',
  'LongHeadlines',
  'Descriptions',
  'BusinessName',
  'CallToAction',
];

export const AssetTempValue = '_PMAX_ASSET_TEMP_';

// Model to ViewModel

const textCollectionModelToViewModelItemMapper = singleFieldName => (item, index) => {
  let text = _.get(item, ['Asset', 'Text'], '');
  // Filter out temp values while model to view model conversion
  if (text.indexOf(AssetTempValue) === 0) {
    text = '';
  }
  return [
    `${singleFieldName}${index}`,
    text,
  ];
};

const textCollectionModelToViewModel = (assetGroupModel, fieldName, singleFieldName) => {
  const texts = _.get(assetGroupModel, fieldName, []);
  const mapper = textCollectionModelToViewModelItemMapper(singleFieldName);

  return {
    [fieldName]: _.chain(texts)
      .map(mapper)
      .object()
      .value(),
  };
};

const imageListModelToViewModel = (assetGroupModel, tempImageIdString) => ({
  Images: {
    PendingImages: tempImageIdString ? _.filter(assetGroupModel.Images, image => _.get(image, ['Asset', 'IdString'], '') !== String(tempImageIdString)) : assetGroupModel.Images,
  },
});

export const assetGroupModelToViewModel = (assetGroupModel, tempImageIdString) => {
  let model = _.pick(assetGroupModel, WhitelistedPropertyKeys);

  model = _.extend(model, textCollectionModelToViewModel(assetGroupModel, 'Headlines', 'Headline'));
  model = _.extend(model, textCollectionModelToViewModel(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
  model = _.extend(model, textCollectionModelToViewModel(assetGroupModel, 'Descriptions', 'Description'));
  model = _.extend(model, imageListModelToViewModel(assetGroupModel, tempImageIdString));

  if (model.BusinessName && model.BusinessName.indexOf(AssetTempValue) === 0) {
    model.BusinessName = '';
  }

  return model;
};

// Suggestions to ViewModel

const getTextsFromSuggestions = ({
  suggestions, fieldName, minCount, maxCount,
}) => {
  const count = Math.min(Math.max(_.size(suggestions), minCount), maxCount);

  return _.chain(count)
    .range()
    .map(index => [`${fieldName}${index}`, _.get(suggestions, [index, 'Asset', 'Text'], '') || _.get(suggestions, [index, 'text'], '')])
    .object()
    .value();
};

const getBusinessNamesFromBiSuggestions = biSuggestions =>
  _.get(biSuggestions, ['businessName'], '');

const getHeadlinesFromRsaSuggestions = rsaSuggestions => getTextsFromSuggestions({
  suggestions: _.get(rsaSuggestions, [0, 'Headlines'], []),
  fieldName: 'Headline',
  minCount: MinHeadlineCount,
  maxCount: MaxHeadlineCount,
});

// Suggestions may not have long headlines, so we use the last description as a fallback
const getLongHeadlinesFromRsaSuggestions = rsaSuggestions => (!_.isEmpty(_.get(rsaSuggestions, [0, 'LongHeadlines'], []))
  ? getTextsFromSuggestions({
    suggestions: _.get(rsaSuggestions, [0, 'LongHeadlines'], []),
    fieldName: 'LongHeadline',
    minCount: MinLongHeadlineCount,
    maxCount: MaxLongHeadlineCount,
  })
  : getTextsFromSuggestions({
    suggestions: [_.last(_.get(rsaSuggestions, [0, 'Descriptions'], []))],
    fieldName: 'LongHeadline',
    minCount: MinLongHeadlineCount,
    maxCount: MinLongHeadlineCount,
  }));

const getDescriptionsFromRsaSuggestions = rsaSuggestions => getTextsFromSuggestions({
  suggestions: _.get(rsaSuggestions, [0, 'Descriptions'], []),
  fieldName: 'Description',
  minCount: MinDescriptionCount,
  maxCount: MaxDescriptionCount,
});

const getImagesFromImageSuggestions = imagesSuggestions => ({
  SuggestedImages: _.take(imagesSuggestions, 5),
});

export const assetGroupViewModelFromSuggestions = ({
  onboardingUrl,
  imagesSuggestions = [],
  rsaSuggestions = [],
  biSuggestions,
}) => ({
  Name: 'Default',
  FinalUrls: [onboardingUrl],
  Images: getImagesFromImageSuggestions(imagesSuggestions),
  Headlines: getHeadlinesFromRsaSuggestions(rsaSuggestions),
  LongHeadlines: getLongHeadlinesFromRsaSuggestions(rsaSuggestions),
  Descriptions: getDescriptionsFromRsaSuggestions(rsaSuggestions),
  BusinessName: getBusinessNamesFromBiSuggestions(biSuggestions),
  CallToAction: 'ActNow',
});

export const assetGroupViewModelFromCache = (cache) => {
  const { businessDetails = {}, recommendations = [], website = '' } = cache;
  const recommendation = _.find(recommendations, rec => rec.campaignType === 'Pmax');

  if (!recommendation) {
    return {
      Name: 'Default',
      FinalUrls: [website],
      BusinessName: getBusinessNamesFromBiSuggestions(businessDetails),
      CallToAction: 'ActNow',
    };
  }

  const {
    imageAssets, shortHeadlines, longHeadlines, descriptions,
  } = recommendation;

  return {
    Name: 'Default',
    FinalUrls: [website],
    Images: getImagesFromImageSuggestions(imageAssets),
    Headlines: getTextsFromSuggestions({
      suggestions: shortHeadlines,
      fieldName: 'Headline',
      minCount: MinHeadlineCount,
      maxCount: MaxHeadlineCount,
    }),
    LongHeadlines: getTextsFromSuggestions({
      suggestions: longHeadlines,
      fieldName: 'LongHeadline',
      minCount: MinLongHeadlineCount,
      maxCount: MaxLongHeadlineCount,
    }),
    Descriptions: getTextsFromSuggestions({
      suggestions: descriptions,
      fieldName: 'Description',
      minCount: MinDescriptionCount,
      maxCount: MaxDescriptionCount,
    }),
    BusinessName: getBusinessNamesFromBiSuggestions(businessDetails),
    CallToAction: 'ActNow',
  };
};

export const getAssetGroupViewModelWithTempAssets = (assetGroupViewModel) => {
  const viewModel = _.clone(assetGroupViewModel);

  if (_.isEmpty(viewModel.BusinessName) || getLength(viewModel.BusinessName, true) > 25) {
    viewModel.BusinessName = `${AssetTempValue}Business`;
  }

  _.each(viewModel.Headlines, (value, key) => {
    if (_.isEmpty(value)) {
      viewModel.Headlines[key] = `${AssetTempValue}${key}`;
    }
  });

  _.each(viewModel.LongHeadlines, (value, key) => {
    if (_.isEmpty(value)) {
      viewModel.LongHeadlines[key] = `${AssetTempValue}${key}`;
    }
  });

  _.each(viewModel.Descriptions, (value, key) => {
    if (_.isEmpty(value)) {
      viewModel.Descriptions[key] = `${AssetTempValue}${key}`;
    }
  });

  return viewModel;
};

// ViewModel to Model

const textCollectionViewModelToModel = (assetGroupViewModel, fieldName) => ({
  [fieldName]: _.chain(assetGroupViewModel)
    .get(fieldName, {})
    .values()
    .filter(value => !_.isEmpty(value))
    .map(value => ({ Asset: { Text: value } }))
    .value(),
});

export const processImageListViewModelToModel = (imageListVm, serverContext, cid, aid) => {
  const imageOData = new ImageOData(cid, aid, getODataConfig(serverContext));
  const flattenedIncludedImageAssetList = getFlattenedImageAssetList(imageListVm, true /* isIncluded */);
  const originalData = {};

  return updateAssetsIdsAsync(flattenedIncludedImageAssetList, 0, imageOData)
    .then((updates) => {
      setMTImageModel(
        originalData,
        updates,
        _.map(imageListVm, imageArray => _.filter(imageArray, imageObj => imageObj.isIncluded)) /* imageList */,
        [] /* UiUnsupportedRatios */
      );
      return originalData;
    });
};

const viewModelToBulkModelForImages = (assetGroupViewModel, serverContext, aid, cid) => {
  const model = processImageListViewModelToModel(assetGroupViewModel.Images.ImageList, serverContext, aid, cid);
  return model;
};

export const assetGroupViewModelToModel = (assetGroupViewModel, serverContext, cid, aid) => {
  let model = _.pick(assetGroupViewModel, WhitelistedPropertyKeys);

  // Force Asset Group status to Active
  model = _.extend(model, { Status: 'Active' });

  model = _.extend(model, textCollectionViewModelToModel(assetGroupViewModel, 'Headlines'));
  model = _.extend(model, textCollectionViewModelToModel(assetGroupViewModel, 'LongHeadlines'));
  model = _.extend(model, textCollectionViewModelToModel(assetGroupViewModel, 'Descriptions'));

  return viewModelToBulkModelForImages(assetGroupViewModel, serverContext, cid, aid)
    .then(imagesModel => _.extend(model, imagesModel));
};

export const getHasAssets = (assetGroupData) => {
  const images = assetGroupData && assetGroupData.Images;
  const headlines = _.compact(_.values(assetGroupData && assetGroupData.Headlines)) || [];
  const longHeadlines = _.compact(_.values((assetGroupData && assetGroupData.LongHeadlines))) || [];
  const descriptions = _.compact(_.values((assetGroupData && assetGroupData.Descriptions))) || [];
  if (images && images.ImageList && images.ImageList.length > 0) {
    return true;
  }

  if (headlines.length > 0 || longHeadlines.length > 0 || descriptions.length > 0) {
    return true;
  }

  return false;
};
